/**
 * Key transfer — SOURCE device (the one that already has the key).
 *
 * Reached from Settings. Under protocol v3 this device *scans first*: the new
 * device shows its ephemeral public key, and only then can this one seal the
 * seed to it. The QR rendered here is a ciphertext that nothing but the new
 * device can open, so photographing it achieves nothing — which is the whole
 * reason the flow runs in this direction.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useRouter } from 'expo-router';
import * as ScreenCapture from 'expo-screen-capture';
import QRCodeView from '../../src/components/QRCodeView';
import { getSeed } from '../../src/utils/crypto';
import { useAuthStore } from '../../src/stores/authStore';
import { sealSeedForRequest, QR_EXPIRY_SECONDS } from '../../src/utils/key-transfer';
import QRScannerView from '../../src/components/QRScannerView';
import { useTheme } from '../../src/theme/ThemeContext';
import { useThemedStyles } from '../../src/theme/useThemedStyles';
import type { Theme } from '../../src/theme/types';

type Step = 'scanning' | 'showing';

export default function TransferSourceScreen() {
  const router = useRouter();
  const [step, setStep] = useState<Step>('scanning');
  const [qrValue, setQrValue] = useState<string | null>(null);
  const [confirmationCode, setConfirmationCode] = useState<string | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(QR_EXPIRY_SECONDS);
  const [expired, setExpired] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const scannedRef = useRef(false);
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();

  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Block screenshots while a transfer code is on screen. Native only — the web
  // build has no equivalent, so a browser tab is not covered by this.
  useEffect(() => {
    if (Platform.OS === 'web' || step !== 'showing') return;
    ScreenCapture.preventScreenCaptureAsync().catch(() => {});
    return () => {
      ScreenCapture.allowScreenCaptureAsync().catch(() => {});
    };
  }, [step]);

  useEffect(() => clearTimer, []);

  const restart = useCallback(() => {
    clearTimer();
    scannedRef.current = false;
    setStep('scanning');
    setQrValue(null);
    setConfirmationCode(null);
    setScanError(null);
    setExpired(false);
    setIsProcessing(false);
    setSecondsLeft(QR_EXPIRY_SECONDS);
  }, []);

  const handleScan = useCallback(async (data: string) => {
    if (scannedRef.current || isProcessing) return;
    scannedRef.current = true;
    setIsProcessing(true);
    setScanError(null);

    try {
      const userId = useAuthStore.getState().user?.id;
      const seed = userId ? await getSeed(userId) : null;
      if (!seed) {
        // The device key may well be here — it is the SEED that is missing,
        // and only the seed can be transferred. Say so, rather than telling
        // someone on a visibly working device that it has no key.
        setScanError(
          'This device holds no transferable key material. Restore it from the recovery phrase first, or enter the phrase on the new device instead.'
        );
        scannedRef.current = false;
        setIsProcessing(false);
        return;
      }

      const sealed = await sealSeedForRequest(data, seed);

      setQrValue(sealed.qr);
      setConfirmationCode(sealed.confirmationCode);
      setSecondsLeft(QR_EXPIRY_SECONDS);
      setExpired(false);
      setStep('showing');
      setIsProcessing(false);

      clearTimer();
      timerRef.current = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            clearTimer();
            setQrValue(null);
            setExpired(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      console.error('Failed to seal transfer:', err);
      setScanError(
        err instanceof Error
          ? err.message
          : 'Could not read that code. Make sure you are scanning the code on your new device.'
      );
      scannedRef.current = false;
      setIsProcessing(false);
    }
  }, [isProcessing]);

  const formatTime = (s: number) => {
    const min = Math.floor(s / 60);
    const sec = s % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };

  if (step === 'scanning') {
    return (
      <QRScannerView
        title="Scan Your New Device"
        subtitle="On the new device, choose Scan QR Code and point this camera at the code it shows."
        onScan={handleScan}
        onBack={() => router.replace('/(tabs)/settings')}
        isProcessing={isProcessing}
        processingLabel="Preparing key..."
        error={scanError}
        onDismissError={() => {
          setScanError(null);
          scannedRef.current = false;
        }}
        testIDPrefix="transfer-source"
      />
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.card}>
        <View style={styles.iconContainer}>
          <Text style={styles.iconText}>{'\u{1F4F1}'}</Text>
        </View>
        <Text style={styles.title}>Transfer Key</Text>
        <Text style={styles.subtitle}>
          Now scan this code on your new device to finish the transfer.
        </Text>

        <View style={styles.qrContainer}>
          {!qrValue && !expired && (
            <ActivityIndicator size="large" color={colors.primary} />
          )}
          {qrValue && (
            <>
              <QRCodeView value={qrValue} size={280} testID="transfer-source.qr-image" />
              <Text style={styles.timer}>Expires in {formatTime(secondsLeft)}</Text>
            </>
          )}
          {expired && (
            <View style={styles.expiredBox}>
              <Text style={styles.expiredText}>
                This transfer code expired. Start again from your new device.
              </Text>
              <TouchableOpacity
                style={styles.regenerateButton}
                onPress={restart}
                activeOpacity={0.7}
                accessibilityLabel="Scan again"
                accessibilityRole="button"
                testID="transfer-source.restart-button"
              >
                <Text style={styles.regenerateButtonText}>Scan Again</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        {confirmationCode && !expired && (
          <View style={styles.codeBox}>
            <Text style={styles.codeLabel}>Confirmation code</Text>
            <Text style={styles.codeValue} testID="transfer-source.confirmation-code">
              {confirmationCode}
            </Text>
            <Text style={styles.codeHelp}>
              Your new device will show this same code. If it shows something
              different, cancel the transfer.
            </Text>
          </View>
        )}

        <TouchableOpacity
          style={styles.doneButton}
          onPress={() => router.replace('/(tabs)/settings')}
          activeOpacity={0.7}
          accessibilityLabel="Done"
          accessibilityRole="button"
          testID="transfer-source.done-button"
        >
          <Text style={styles.doneButtonText}>Done</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const createStyles = (theme: Theme) => ({
  container: {
    flex: 1 as const,
    backgroundColor: theme.colors.brandedBackground,
    justifyContent: 'center' as const,
    paddingHorizontal: 16,
  },
  card: {
    backgroundColor: theme.colors.surface,
    borderRadius: 16,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 5,
  },
  iconContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: theme.colors.primaryBg,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    alignSelf: 'center' as const,
    marginBottom: 16,
  },
  iconText: { fontSize: 28 },
  title: {
    fontSize: 20,
    fontWeight: '700' as const,
    color: theme.colors.textPrimary,
    textAlign: 'center' as const,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    color: theme.colors.textMuted,
    textAlign: 'center' as const,
    marginBottom: 20,
    lineHeight: 20,
  },
  qrContainer: {
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    minHeight: 300,
    marginBottom: 16,
  },
  timer: {
    fontSize: 14,
    color: theme.colors.textMuted,
    fontWeight: '600' as const,
    marginTop: 12,
  },
  expiredBox: {
    alignItems: 'center' as const,
  },
  expiredText: {
    fontSize: 14,
    color: theme.colors.errorText,
    marginBottom: 12,
    textAlign: 'center' as const,
  },
  regenerateButton: {
    backgroundColor: theme.colors.primary,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 20,
  },
  regenerateButtonText: {
    color: theme.colors.textOnPrimary,
    fontSize: 14,
    fontWeight: '600' as const,
  },
  codeBox: {
    borderWidth: 1,
    borderColor: theme.colors.borderMedium,
    borderRadius: 10,
    padding: 14,
    marginBottom: 16,
    alignItems: 'center' as const,
  },
  codeLabel: {
    fontSize: 12,
    color: theme.colors.textMuted,
    textTransform: 'uppercase' as const,
    letterSpacing: 1,
    marginBottom: 4,
  },
  codeValue: {
    fontSize: 30,
    fontWeight: '700' as const,
    letterSpacing: 6,
    color: theme.colors.textPrimary,
    marginBottom: 6,
  },
  codeHelp: {
    fontSize: 12,
    color: theme.colors.textDisabled,
    textAlign: 'center' as const,
    lineHeight: 18,
  },
  doneButton: {
    borderWidth: 1,
    borderColor: theme.colors.borderMedium,
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: 'center' as const,
  },
  doneButtonText: {
    color: theme.colors.textSecondary,
    fontSize: 16,
    fontWeight: '600' as const,
  },
});
